import { useState } from 'react';
import SubPageHeader from '../SubPageHeader';
import SettingsToggle from '../SettingsToggle';

const CATEGORIES = [
  { key: 'visits', icon: '📍', label: 'الزيارات', items: ['تعيين مسار جديد', 'تذكير بالزيارة', 'تنبيه الزيارات الفائتة', 'نسيت تسجيل الخروج'] },
  { key: 'orders', icon: '🛒', label: 'أوامر البيع', items: ['تأكيد الطلب', 'رفض الطلب', 'طلب يحتاج موافقة', 'مسودات معلقة'] },
  { key: 'collections', icon: '💰', label: 'التحصيلات', items: ['تذكير السداد', 'تحذير الشيك المستحق', 'شيك مرتجع', 'فواتير متأخرة'] },
  { key: 'performance', icon: '📊', label: 'الأداء', items: ['تقدم الهدف اليومي', 'ملخص نهاية اليوم', 'تقرير أسبوعي', 'إنجاز هدف'] },
  { key: 'sync', icon: '🔄', label: 'المزامنة', items: ['أخطاء المزامنة', 'تحديث متاح'] },
  { key: 'system', icon: '⚙️', label: 'النظام', items: ['وضع الصيانة', 'تنبيهات الأمان'] },
];

function useNotifPref(key, def = true) {
  const storageKey = `notif_${key}`;
  const [val, setVal] = useState(() => {
    const s = localStorage.getItem(storageKey);
    return s === null ? def : s === 'true';
  });
  const set = (v) => { setVal(v); localStorage.setItem(storageKey, String(v)); };
  return [val, set];
}

export default function NotificationsSection({ onBack }) {
  const [masterOn, setMasterOn] = useNotifPref('master', true);
  const [quietOn, setQuietOn] = useNotifPref('quiet', false);
  const [quietFrom, setQuietFrom] = useState(localStorage.getItem('notif_quiet_from') || '22:00');
  const [quietTo, setQuietTo] = useState(localStorage.getItem('notif_quiet_to') || '07:00');

  return (
    <div className="flex flex-col min-h-full" dir="rtl">
      <SubPageHeader title="الإشعارات" onBack={onBack} />
      <div className="p-4 space-y-4">

        {/* Master Toggle */}
        <div className="bg-card border border-border rounded-2xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🔔</span>
            <div>
              <p className="font-bold text-sm text-foreground">الإشعارات</p>
              <p className="text-xs text-muted-foreground">تفعيل جميع الإشعارات</p>
            </div>
          </div>
          <SettingsToggle enabled={masterOn} onToggle={setMasterOn} />
        </div>

        {/* Categories */}
        {CATEGORIES.map(cat => (
          <CategoryBlock key={cat.key} cat={cat} masterOn={masterOn} />
        ))}

        {/* Quiet Hours */}
        <div className={`bg-card border border-border rounded-2xl p-4 space-y-3 ${!masterOn ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-lg">🌙</span>
              <div>
                <p className="font-bold text-sm text-foreground">ساعات الهدوء</p>
                <p className="text-xs text-muted-foreground">إيقاف الإشعارات ليلاً</p>
              </div>
            </div>
            <SettingsToggle enabled={quietOn} onToggle={setQuietOn} />
          </div>
          {quietOn && (
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">من</label>
                <input type="time" value={quietFrom} onChange={e => { setQuietFrom(e.target.value); localStorage.setItem('notif_quiet_from', e.target.value); }}
                  className="w-full border border-border rounded-xl p-2 text-sm bg-background text-foreground text-center" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">إلى</label>
                <input type="time" value={quietTo} onChange={e => { setQuietTo(e.target.value); localStorage.setItem('notif_quiet_to', e.target.value); }}
                  className="w-full border border-border rounded-xl p-2 text-sm bg-background text-foreground text-center" />
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

function CategoryBlock({ cat, masterOn }) {
  const [expanded, setExpanded] = useState(false);
  const [catOn, setCatOn] = useState(() => {
    const s = localStorage.getItem(`notif_cat_${cat.key}`);
    return s === null ? true : s === 'true';
  });

  const toggle = (v) => {
    setCatOn(v);
    localStorage.setItem(`notif_cat_${cat.key}`, String(v));
  };

  return (
    <div className={`bg-card border border-border rounded-2xl overflow-hidden transition-opacity ${!masterOn ? 'opacity-50 pointer-events-none' : ''}`}>
      <div className="p-4 flex items-center gap-3">
        <span className="text-xl">{cat.icon}</span>
        <div className="flex-1">
          <p className="font-bold text-sm text-foreground">{cat.label}</p>
          <button onClick={() => setExpanded(e => !e)} className="text-xs text-primary mt-0.5">
            {expanded ? 'إخفاء التفاصيل' : 'تفاصيل ▾'}
          </button>
        </div>
        <SettingsToggle enabled={catOn} onToggle={toggle} />
      </div>
      {expanded && catOn && (
        <div className="border-t border-border bg-muted/20 px-4 py-2 space-y-2">
          {cat.items.map(item => (
            <ItemToggle key={item} label={item} storageKey={`notif_item_${cat.key}_${item}`} />
          ))}
        </div>
      )}
    </div>
  );
}

function ItemToggle({ label, storageKey }) {
  const [on, setOn] = useState(() => {
    const s = localStorage.getItem(storageKey);
    return s === null ? true : s === 'true';
  });
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-xs text-foreground">{label}</span>
      <SettingsToggle enabled={on} onToggle={v => { setOn(v); localStorage.setItem(storageKey, String(v)); }} />
    </div>
  );
}